import { useState, useEffect } from 'react';
import { RefreshCw, AlertCircle, MapPin, DollarSign, Loader2, ExternalLink, Building2, Globe, Link as LinkIcon, IdCard, Database, X, CheckCircle, Server } from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import { ConnectionCard } from './ConnectionCard';
import { PublishLocationsResultPanel } from './PublishLocationsResultPanel';
import { PublishTariffsResultPanel } from './PublishTariffsResultPanel';
import { ExternalKioskSetupForm } from './ExternalKioskSetupForm';
import { ExternalVpostSetupForm } from './ExternalVpostSetupForm';
import { environmentConfig } from '@/core/api-config';
import type { ConnectionStatus, PublishLocationsResult, PublishTariffsResult } from '../../types/connection-status-types';
import type { CpoType, ExternalCpoConnectionRequestDto, VpostConnectionRequestDto } from '../../types/api-types';
import type { OcpiModule } from '../../use-cases/SyncCpoOcpiDataUseCase';

// Deep link to operator in Nayax Core (environment-aware)
const getOperatorDeepLink = (operatorId: number): string => {
  const portalUrl = environmentConfig.getPortalUrl();
  return `${portalUrl}/core/public/f?model=administration/Actors#${operatorId}`;
};

// Which external connection type the user is setting up
type ExternalSetupType = 'kiosk' | 'vpost' | null;

interface ConnectionStatusDashboardProps {
  connectionStatus: ConnectionStatus;
  cpoType: CpoType | null;
  onRefresh: () => void;
  onPublishLocations?: () => void;
  onPublishTariffs?: () => void;
  onSyncEves?: (publishToOcpi: boolean) => Promise<boolean>;
  onSetupKiosk?: () => void;
  onSetupExternalKiosk?: (data: ExternalCpoConnectionRequestDto) => Promise<void>;
  onSetupVpost?: (data: VpostConnectionRequestDto) => Promise<void>;
  onLoadCsmsNames?: () => Promise<string[]>;
  onLoadKioskCsmsNames: () => Promise<string[]>;
  onSyncCpoModule?: (connectionId: number, module: OcpiModule) => void;
  syncingCpoModules?: Set<string>;
  onClearLocationsResult?: () => void;
  onClearTariffsResult?: () => void;
  onClearSyncEvesResult?: () => void;
  isLoading: boolean;
  isRefreshing: boolean;
  isPublishingLocations?: boolean;
  isPublishingTariffs?: boolean;
  isSyncingEves?: boolean;
  isSettingUpKiosk?: boolean;
  isSettingUpExternalKiosk?: boolean;
  isSettingUpVpost?: boolean;
  publishLocationsResult?: PublishLocationsResult | null;
  publishTariffsResult?: PublishTariffsResult | null;
  syncEvesSuccess?: boolean | null;
  externalKioskError?: string | null;
  vpostError?: string | null;
}

export function ConnectionStatusDashboard({
  connectionStatus,
  cpoType,
  onRefresh,
  onPublishLocations,
  onPublishTariffs,
  onSyncEves,
  onSetupKiosk,
  onSetupExternalKiosk,
  onSetupVpost,
  onLoadCsmsNames,
  onLoadKioskCsmsNames,
  onSyncCpoModule,
  syncingCpoModules,
  onClearLocationsResult,
  onClearTariffsResult,
  onClearSyncEvesResult,
  isLoading,
  isRefreshing,
  isPublishingLocations = false,
  isPublishingTariffs = false,
  isSyncingEves = false,
  isSettingUpKiosk = false,
  isSettingUpExternalKiosk = false,
  isSettingUpVpost = false,
  publishLocationsResult,
  publishTariffsResult,
  syncEvesSuccess,
  externalKioskError,
  vpostError,
}: ConnectionStatusDashboardProps) {
  // Which external connection type the user has selected to set up (null = type selector shown)
  const [externalSetupType, setExternalSetupType] = useState<ExternalSetupType>(null);

  // Auto-close the setup form when the connection it was setting up becomes active.
  // Use connectionStatus.connections directly (not the destructured alias which is declared below).
  useEffect(() => {
    const conns = connectionStatus.connections;
    if (externalSetupType === 'kiosk' && conns.some(c => c.type === 'Kiosk')) {
      setExternalSetupType(null);
    }
    if (externalSetupType === 'vpost' && conns.some(c => c.type === 'OcpiToVpost')) {
      setExternalSetupType(null);
    }
  }, [connectionStatus.connections, externalSetupType]);
  // State for showing sync EVEs form
  const [showSyncEvesForm, setShowSyncEvesForm] = useState(false);
  const [syncPublishToOcpi, setSyncPublishToOcpi] = useState(true);

  if (isLoading) {
    return <LoadingState />;
  }

  const { connections } = connectionStatus;
  const isExternalCpo = cpoType === 'external';

  // Granular connection checks for External CPO
  const hasKioskConnection = connections.some(c => c.type === 'Kiosk');
  const hasVpostConnection = connections.some(c => c.type === 'OcpiToVpost');
  // Generic active connection check (used for Internal CPO publish logic)
  const hasActiveConnection = connections.some(c => c.registered);
  // Only internal CPO can publish
  const canPublish = hasActiveConnection && !isExternalCpo;

  // Handler for external kiosk setup form submission
  const handleExternalKioskSubmit = async (data: ExternalCpoConnectionRequestDto) => {
    if (onSetupExternalKiosk) {
      await onSetupExternalKiosk(data);
      setExternalSetupType(null);
    }
  };

  // Handler for VPOST setup form submission
  const handleVpostSubmit = async (data: VpostConnectionRequestDto) => {
    if (onSetupVpost) {
      await onSetupVpost(data);
      setExternalSetupType(null);
    }
  };

  // Handler for sync EVEs form submission
  const handleSyncEvesSubmit = async () => {
    if (onSyncEves) {
      await onSyncEves(syncPublishToOcpi);
      setShowSyncEvesForm(false);
      setSyncPublishToOcpi(true);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with operator info and actions */}
      <div className="bg-white border rounded-lg p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <span>Operator:</span>
              <a
                href={getOperatorDeepLink(connectionStatus.operatorId)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1"
              >
                {connectionStatus.operatorName}
                <ExternalLink className="h-4 w-4" />
              </a>
            </h2>
            <div className="flex items-center gap-4 flex-wrap">
              {/* OCPI ID Badge */}
              {connectionStatus.ocpiId && connectionStatus.ocpiId.partyId && (
                <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 text-gray-700">
                  <IdCard className="h-4 w-4" />
                  <span className="text-sm font-medium font-mono">
                    {connectionStatus.ocpiId.countryCode}-{connectionStatus.ocpiId.partyId}
                  </span>
                </div>
              )}
              {/* CPO Type Badge */}
              {cpoType && (
                <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${
                  isExternalCpo
                    ? 'bg-purple-50 text-purple-700'
                    : 'bg-blue-50 text-blue-700'
                }`}>
                  {isExternalCpo ? (
                    <Globe className="h-4 w-4" />
                  ) : (
                    <Building2 className="h-4 w-4" />
                  )}
                  <span className="text-sm font-medium">
                    {isExternalCpo ? 'External CPO' : 'Internal CPO'}
                  </span>
                </div>
              )}
              <ConnectionsSummary connections={connections} />
              <div className="text-sm text-gray-500">
                Last checked: {new Date(connectionStatus.lastChecked).toLocaleString()}
              </div>
            </div>
          </div>

          <Button
            variant="outline"
            onClick={onRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>

        {/* External CPO — add connection options (shown when at least one connection type is missing) */}
        {isExternalCpo && (!hasKioskConnection || !hasVpostConnection) &&
          ((!hasKioskConnection && onSetupExternalKiosk) || (!hasVpostConnection && onSetupVpost && onLoadCsmsNames)) && (
          <div className="mt-6 pt-6 border-t">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Add Connection</h3>

            {externalSetupType === null && (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">
                  {!hasKioskConnection && !hasVpostConnection
                    ? 'Choose how to connect your external OCPI system:'
                    : 'Add another connection type:'}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Connect to Nayax Kiosk — only shown if no Kiosk connection yet */}
                  {!hasKioskConnection && onSetupExternalKiosk && (
                    <button
                      type="button"
                      onClick={() => setExternalSetupType('kiosk')}
                      className="flex items-start gap-3 p-4 bg-purple-50 border border-purple-200 rounded-lg text-left hover:bg-purple-100 hover:border-purple-300 transition-colors"
                    >
                      <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0">
                        <LinkIcon className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-purple-900">Connect to Nayax Kiosk</p>
                        <p className="text-xs text-purple-700 mt-1">
                          Connect your OCPI system to the Nayax Kiosk network for roaming services.
                        </p>
                      </div>
                    </button>
                  )}

                  {/* Connect to OCPI-to-VPOST — only shown if no VPOST connection yet */}
                  {!hasVpostConnection && onSetupVpost && onLoadCsmsNames && (
                    <button
                      type="button"
                      onClick={() => setExternalSetupType('vpost')}
                      className="flex items-start gap-3 p-4 bg-purple-50 border border-purple-200 rounded-lg text-left hover:bg-purple-100 hover:border-purple-300 transition-colors"
                    >
                      <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0">
                        <Server className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-purple-900">Connect to OCPI-to-VPOST</p>
                        <p className="text-xs text-purple-700 mt-1">
                          Connect your OCPI system to the Nayax VPOST network.
                        </p>
                      </div>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* External CPO Info Panel - shown only when both connections exist */}
        {isExternalCpo && hasKioskConnection && hasVpostConnection && (
          <div className="mt-6 pt-6 border-t">
            <div className="flex items-start gap-3 p-4 bg-purple-50 rounded-lg">
              <Globe className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-semibold text-purple-900">External CPO Configuration</h3>
                <p className="text-sm text-purple-700 mt-1">
                  This operator is configured as an External CPO with both Kiosk and OCPI-to-VPOST connections active.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Operator-level Publish Actions */}
        {canPublish && (
          <div className="mt-6 pt-6 border-t">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Publish Actions</h3>
            <div className="flex gap-3 flex-wrap">
              {onPublishLocations && (
                <Button
                  variant="default"
                  onClick={onPublishLocations}
                  disabled={isPublishingLocations || isPublishingTariffs || isSyncingEves}
                  className="bg-[#FFCD00] hover:bg-[#E6B800] text-black"
                >
                  {isPublishingLocations ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <MapPin className="h-4 w-4 mr-2" />
                  )}
                  {isPublishingLocations ? 'Publishing Locations...' : 'Publish Locations'}
                </Button>
              )}
              {onPublishTariffs && (
                <Button
                  variant="default"
                  onClick={onPublishTariffs}
                  disabled={isPublishingLocations || isPublishingTariffs || isSyncingEves}
                  className="bg-[#FFCD00] hover:bg-[#E6B800] text-black"
                >
                  {isPublishingTariffs ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <DollarSign className="h-4 w-4 mr-2" />
                  )}
                  {isPublishingTariffs ? 'Publishing Tariffs...' : 'Publish Tariffs'}
                </Button>
              )}
              {onSyncEves && (
                <Button
                  variant="default"
                  onClick={() => setShowSyncEvesForm(true)}
                  disabled={isPublishingLocations || isPublishingTariffs || isSyncingEves}
                  className="bg-[#FFCD00] hover:bg-[#E6B800] text-black"
                >
                  {isSyncingEves ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Database className="h-4 w-4 mr-2" />
                  )}
                  {isSyncingEves ? 'Syncing Data...' : 'Sync Data'}
                </Button>
              )}
            </div>
            {!hasActiveConnection && (
              <p className="text-sm text-gray-500 mt-2">
                At least one registered connection is required to publish.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Sync EVSEs Form - shown when button is clicked (appears first so it's always visible at top) */}
      {showSyncEvesForm && onSyncEves && (
        <div className="bg-white border rounded-lg p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Sync Data</h3>
              <p className="text-sm text-gray-600 mt-1">
                Synchronize EVSE data for this operator.
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowSyncEvesForm(false);
                setSyncPublishToOcpi(true);
              }}
              disabled={isSyncingEves}
              className="h-8 w-8 p-0 -mt-1 -mr-2"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="publishToOcpi"
                checked={syncPublishToOcpi}
                onChange={(e) => setSyncPublishToOcpi(e.target.checked)}
                disabled={isSyncingEves}
                className="h-4 w-4 text-[#FFCD00] border-gray-300 rounded focus:ring-[#FFCD00] disabled:opacity-50"
              />
              <label htmlFor="publishToOcpi" className="text-sm text-gray-700">
                Publish to OCPI after sync
              </label>
            </div>
            <div className="flex gap-3">
              <Button
                variant="default"
                onClick={handleSyncEvesSubmit}
                disabled={isSyncingEves}
                className="bg-[#FFCD00] hover:bg-[#E6B800] text-black"
              >
                {isSyncingEves ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <Database className="h-4 w-4 mr-2" />
                    Start Sync
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowSyncEvesForm(false);
                  setSyncPublishToOcpi(true);
                }}
                disabled={isSyncingEves}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Publish Results - shown when available */}
      {publishLocationsResult && (
        <PublishLocationsResultPanel
          result={publishLocationsResult}
          onClose={onClearLocationsResult}
        />
      )}
      {publishTariffsResult && (
        <PublishTariffsResultPanel
          result={publishTariffsResult}
          onClose={onClearTariffsResult}
        />
      )}
      {syncEvesSuccess === true && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-6 w-6 text-green-600" />
              <div>
                <h3 className="text-lg font-semibold text-green-800">
                  <Database className="h-5 w-5 inline mr-2" />
                  Sync Data Completed
                </h3>
                <p className="text-sm text-green-700 mt-1">
                  EVSE data synchronized successfully.
                </p>
              </div>
            </div>
            {onClearSyncEvesResult && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearSyncEvesResult}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      )}

      {/* External Kiosk Setup Form - shown when user selects kiosk type */}
      {externalSetupType === 'kiosk' && onSetupExternalKiosk && (
        <ExternalKioskSetupForm
          onSubmit={handleExternalKioskSubmit}
          onCancel={() => setExternalSetupType(null)}
          onLoadCsmsNames={onLoadKioskCsmsNames}
          isLoading={isSettingUpExternalKiosk}
          error={externalKioskError}
        />
      )}

      {/* VPOST Setup Form - shown when user selects VPOST type */}
      {externalSetupType === 'vpost' && onSetupVpost && onLoadCsmsNames && (
        <ExternalVpostSetupForm
          onSubmit={handleVpostSubmit}
          onCancel={() => setExternalSetupType(null)}
          onLoadCsmsNames={onLoadCsmsNames}
          isLoading={isSettingUpVpost}
          error={vpostError}
        />
      )}

      {/* Connections List */}
      {connections.length > 0 ? (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Connections</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {connections.map((connection, index) => (
              <ConnectionCard
                key={`${connection.type}-${connection.partner?.id?.partyId || index}`}
                connection={connection}
                isExternalCpo={isExternalCpo}
                syncingModules={syncingCpoModules}
                onSyncModule={isExternalCpo ? onSyncCpoModule : undefined}
              />
            ))}
          </div>
        </div>
      ) : (
        <EmptyState
          onSetupKiosk={onSetupKiosk}
          isSettingUpKiosk={isSettingUpKiosk}
          isExternalCpo={isExternalCpo}
        />
      )}
    </div>
  );
}

function ConnectionsSummary({
  connections,
}: {
  connections: ConnectionStatus['connections'];
}) {
  const registered = connections.filter((c) => c.registered).length;
  const pending = connections.length - registered;

  if (connections.length === 0) {
    return (
      <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-full">
        <span className="w-2 h-2 rounded-full bg-gray-400" />
        <span className="text-sm text-gray-600">No connections</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2 px-3 py-1 bg-green-50 rounded-full">
        <span className="w-2 h-2 rounded-full bg-green-500" />
        <span className="text-sm text-green-700">{registered} registered</span>
      </div>
      {pending > 0 && (
        <div className="flex items-center gap-2 px-3 py-1 bg-yellow-50 rounded-full">
          <span className="w-2 h-2 rounded-full bg-yellow-500" />
          <span className="text-sm text-yellow-700">{pending} pending</span>
        </div>
      )}
    </div>
  );
}

function EmptyState({
  onSetupKiosk,
  isSettingUpKiosk,
  isExternalCpo,
}: {
  onSetupKiosk?: () => void;
  isSettingUpKiosk: boolean;
  isExternalCpo: boolean;
}) {
  // External CPO with no connections — type selector in the header handles setup, no empty state needed
  if (isExternalCpo) {
    return null;
  }

  return (
    <div className="bg-white border rounded-lg p-12 text-center">
      <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
      <h3 className="text-lg font-semibold text-gray-900 mb-2">No Connections Found</h3>
      <p className="text-gray-600 mb-6 max-w-md mx-auto">
        This operator does not have any OCPI connections configured yet.
      </p>
      {onSetupKiosk && (
        <Button
          onClick={onSetupKiosk}
          disabled={isSettingUpKiosk}
          className="bg-[#FFCD00] hover:bg-[#E6B800] text-black"
        >
          {isSettingUpKiosk ? 'Setting Up...' : 'Setup Kiosk Connection'}
        </Button>
      )}
    </div>
  );
}

function LoadingState() {
  return (
    <div className="space-y-6">
      <div className="bg-white border rounded-lg p-6 animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/3 mb-4" />
        <div className="h-6 bg-gray-200 rounded w-1/4" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white border rounded-lg p-6 animate-pulse h-48">
            <div className="h-6 bg-gray-200 rounded w-1/2 mb-4" />
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 rounded w-full" />
              <div className="h-4 bg-gray-200 rounded w-3/4" />
              <div className="h-4 bg-gray-200 rounded w-5/6" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
